import { useEffect, useState } from "react";
import { API, useCurrency } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Pencil, Trash2, Gift, Loader2, Clock, Percent, Search, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import SegmentMultiSelect from "../../components/SegmentMultiSelect";

export default function AdminPlans() {
  const { symbol } = useCurrency();
  const [plans, setPlans] = useState([]);
  const [taxGroups, setTaxGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    name: "",
    status: "all",
    popular: "all",
  });

  const [formData, setFormData] = useState({
    name: "",
    amount: "",
    credits: "",
    bonus_credits: "0",
    tax_group_id: "",
    expiry_days: "",
    is_active: true,
    is_popular: false,
    allowed_segment_ids: [],
  });

  useEffect(() => {
    fetchPlans();
    fetchTaxGroups();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${API}/plans/all`, { credentials: "include" });
      if (response.ok) {
        setPlans(await response.json());
      }
    } catch (error) {
      console.error("Failed to fetch plans:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaxGroups = async () => {
    try {
      const response = await fetch(`${API}/tax-groups/active`, { credentials: "include" });
      if (response.ok) setTaxGroups(await response.json());
    } catch (error) {
      console.error("Failed to fetch tax groups:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    const payload = {
      ...formData,
      amount: parseFloat(formData.amount),
      credits: parseFloat(formData.credits),
      bonus_credits: parseFloat(formData.bonus_credits) || 0,
      tax_group_id: formData.tax_group_id === "none" ? null : formData.tax_group_id || null,
      expiry_days: formData.expiry_days ? parseInt(formData.expiry_days) : null,
      allowed_segment_ids: formData.allowed_segment_ids || [],
    };

    try {
      const url = editingPlan
        ? `${API}/plans/${editingPlan.plan_id}`
        : `${API}/plans`;
      const method = editingPlan ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to save plan");

      toast.success(editingPlan ? "Plan updated!" : "Plan created!");
      setIsDialogOpen(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save plan");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (planId) => {
    if (!confirm("Are you sure you want to delete this plan?")) return;

    try {
      const response = await fetch(`${API}/plans/${planId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) throw new Error("Failed to delete");

      toast.success("Plan deleted!");
      fetchPlans();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete plan");
    }
  };

  const openEditDialog = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      amount: plan.amount.toString(),
      credits: plan.credits.toString(),
      bonus_credits: plan.bonus_credits.toString(),
      tax_group_id: plan.tax_group_id || "",
      expiry_days: plan.expiry_days ? plan.expiry_days.toString() : "",
      is_active: plan.is_active,
      is_popular: plan.is_popular,
      allowed_segment_ids: plan.allowed_segment_ids || [],
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPlan(null);
    setFormData({
      name: "",
      amount: "",
      credits: "",
      bonus_credits: "0",
      tax_group_id: "",
      expiry_days: "",
      is_active: true,
      is_popular: false,
      allowed_segment_ids: [],
    });
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-plans">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Wallet Plans</h1>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-green-500 hover:bg-green-600" data-testid="add-plan-btn">
                <Plus size={18} className="mr-2" />
                Add Plan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-white max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingPlan ? "Edit Plan" : "Add New Plan"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Plan Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Starter Pack"
                    required
                    className="bg-white"
                    data-testid="plan-form-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Amount ({symbol})</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="10.00"
                      required
                      className="bg-white"
                      data-testid="plan-form-amount"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">User pays this</p>
                  </div>
                  <div>
                    <Label>Credits</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.credits}
                      onChange={(e) => setFormData({ ...formData, credits: e.target.value })}
                      placeholder="12.00"
                      required
                      className="bg-white"
                      data-testid="plan-form-credits"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">User gets this</p>
                  </div>
                </div>
                <div>
                  <Label>Bonus Credits</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.bonus_credits}
                    onChange={(e) => setFormData({ ...formData, bonus_credits: e.target.value })}
                    placeholder="0.00"
                    className="bg-white"
                    data-testid="plan-form-bonus"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Extra bonus (included in total credits)</p>
                </div>

                {/* Tax Group */}
                <div>
                  <Label>Tax Group</Label>
                  <Select
                    value={formData.tax_group_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, tax_group_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger className="bg-white" data-testid="plan-form-tax-group">
                      <SelectValue placeholder="Select tax group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Tax</SelectItem>
                      {taxGroups.map((group) => (
                        <SelectItem key={group.group_id} value={group.group_id}>
                          {group.name} ({group.taxes?.map(t => `${t.name} ${t.percentage}%`).join(", ")})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-gray-400 mt-1">Tax applied on recharge amount</p>
                </div>

                {/* Expiry Days */}
                <div>
                  <Label>Credit Expiry (Days)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={formData.expiry_days}
                    onChange={(e) => setFormData({ ...formData, expiry_days: e.target.value })}
                    placeholder="Leave empty for no expiry"
                    className="bg-white"
                    data-testid="plan-form-expiry"
                  />
                  <p className="text-[10px] text-gray-400 mt-1">Credits expire after this many days. Leave empty for no expiry.</p>
                </div>

                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="plan-form-active"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Mark as Popular</Label>
                  <Switch
                    checked={formData.is_popular}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_popular: checked })}
                    data-testid="plan-form-popular"
                  />
                </div>

                <SegmentMultiSelect
                  value={formData.allowed_segment_ids}
                  onChange={(ids) => setFormData({ ...formData, allowed_segment_ids: ids })}
                  testIdPrefix="plan-form-segments"
                />
                <Button
                  type="submit"
                  className="w-full bg-green-500 hover:bg-green-600"
                  disabled={formLoading}
                  data-testid="plan-form-submit"
                >
                  {formLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : editingPlan ? (
                    "Update Plan"
                  ) : (
                    "Add Plan"
                  )}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filter Bar */}
        <div className="mb-6 space-y-3" data-testid="plans-filter-bar">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search plans..."
                value={filters.name}
                onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                className="pl-11 bg-white"
                data-testid="plans-filter-name"
              />
            </div>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`gap-2 ${(filters.status !== "all" || filters.popular !== "all") ? "border-green-500 text-green-600" : ""}`}
              data-testid="plans-filter-toggle"
            >
              <SlidersHorizontal size={16} />
              Filters
            </Button>
            {(filters.name || filters.status !== "all" || filters.popular !== "all") && (
              <Button variant="ghost" size="sm" onClick={() => setFilters({ name: "", status: "all", popular: "all" })} className="text-gray-500" data-testid="plans-filter-clear">
                <X size={14} className="mr-1" /> Clear
              </Button>
            )}
          </div>
          {showFilters && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 gap-3 animate-fade-in">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Status</Label>
                <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
                  <SelectTrigger className="bg-white h-9 text-sm" data-testid="plans-filter-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Popular</Label>
                <Select value={filters.popular} onValueChange={(v) => setFilters({ ...filters, popular: v })}>
                  <SelectTrigger className="bg-white h-9 text-sm" data-testid="plans-filter-popular"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="yes">Popular Only</SelectItem>
                    <SelectItem value="no">Non-Popular</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        {/* Plans Grid */}
        {(() => {
          const filteredPlans = plans.filter((plan) => {
            if (filters.name && !plan.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
            if (filters.status === "active" && !plan.is_active) return false;
            if (filters.status === "inactive" && plan.is_active) return false;
            if (filters.popular === "yes" && !plan.is_popular) return false;
            if (filters.popular === "no" && plan.is_popular) return false;
            return true;
          });
          return loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-48 w-full rounded-xl"></div>
            ))}
          </div>
        ) : filteredPlans.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <p className="text-gray-500">No wallet plans found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredPlans.map((plan) => (
              <div
                key={plan.plan_id}
                className={`bg-white rounded-2xl border-2 p-6 ${
                  plan.is_popular ? "border-green-500" : "border-gray-100"
                }`}
                data-testid={`plan-card-${plan.plan_id}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    {plan.is_popular && (
                      <span className="bg-green-500 text-white text-xs font-medium px-2 py-1 rounded-full mb-2 inline-block">
                        Popular
                      </span>
                    )}
                    <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                    <p className={`text-sm ${plan.is_active ? "text-green-600" : "text-gray-400"}`}>
                      {plan.is_active ? "Active" : "Inactive"}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(plan)}
                      data-testid={`edit-plan-${plan.plan_id}`}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => handleDelete(plan.plan_id)}
                      data-testid={`delete-plan-${plan.plan_id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Pay</span>
                    <span className="font-medium text-gray-900">{symbol}{Math.round(plan.amount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Get</span>
                    <span className="font-bold text-green-600 text-xl">{symbol}{Math.round(plan.credits)}</span>
                  </div>
                  {plan.bonus_credits > 0 && (
                    <div className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-2 rounded-lg mt-3">
                      <Gift size={16} />
                      <span className="text-sm font-medium">
                        +{symbol}{Math.round(plan.bonus_credits)} bonus
                      </span>
                    </div>
                  )}
                  {plan.tax_group && (
                    <div className="flex items-center gap-2 bg-orange-50 text-orange-700 px-3 py-2 rounded-lg mt-2" data-testid={`plan-tax-${plan.plan_id}`}>
                      <Percent size={14} />
                      <span className="text-xs font-medium">
                        {plan.tax_group.name}: {plan.tax_group.taxes?.map(t => `${t.name} ${t.percentage}%`).join(", ")}
                      </span>
                    </div>
                  )}
                  {plan.expiry_days && (
                    <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-2 rounded-lg mt-2" data-testid={`plan-expiry-${plan.plan_id}`}>
                      <Clock size={14} />
                      <span className="text-xs font-medium">
                        Credits expire in {plan.expiry_days} days
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
        })()}
      </div>
    </AdminLayout>
  );
}
